/**
 * Tab Sticky Notes Types
 *
 * Type definitions for per-tab sticky notes with PostgreSQL persistence.
 * Each tab (e.g., /app/epg, /app/notes) can have its own sticky notes.
 */

import type { StickyColor, StickyColorScheme } from './userStickies';

// Re-export color types for convenience
export type { StickyColor, StickyColorScheme };
export { stickyColorSchemes, stickyColorOrder } from './userStickies';

/**
 * Tab Sticky Note data model (PostgreSQL-backed)
 */
export interface TabStickyNote {
  id: string;              // UUID from PostgreSQL
  user_id: string;         // User identifier
  tab_path: string;        // Tab route path (e.g., "/app/epg")
  title: string;           // Editable title (default: "New Note")
  content: string;         // Note content
  color: StickyColor;      // Color theme
  position_x: number;      // X position in pixels
  position_y: number;      // Y position in pixels
  width: number;           // Width in pixels
  height: number;          // Height in pixels
  z_index: number;         // Stacking order
  is_collapsed: boolean;   // Whether collapsed to pill
  created_at: string | null;  // ISO timestamp
  updated_at: string | null;  // ISO timestamp
}

/**
 * Request payload for creating a sticky note
 */
export interface TabStickyCreateRequest {
  tab_path: string;
  title?: string;
  content?: string;
  color?: StickyColor;
  position_x?: number;
  position_y?: number;
  width?: number;
  height?: number;
}

/**
 * Request payload for updating a sticky note
 * All fields are optional - only include fields being updated
 */
export interface TabStickyUpdateRequest {
  title?: string;
  content?: string;
  color?: StickyColor;
  position_x?: number;
  position_y?: number;
  width?: number;
  height?: number;
  z_index?: number;
  is_collapsed?: boolean;
}

/**
 * Response from GET /tab-stickies
 */
export interface TabStickiesListResponse {
  stickies: TabStickyNote[];
}

/**
 * Response from bulk sync
 */
export interface TabStickyBulkSyncResponse {
  synced: number;
  created: number;
  updated: number;
}

/**
 * Generic success response
 */
export interface TabStickySuccessResponse {
  success: boolean;
  id?: string;
}

/**
 * Default values for new stickies
 */
export const TAB_STICKY_DEFAULTS = {
  TITLE: 'New Note',
  CONTENT: '',
  COLOR: 'green' as StickyColor,
  POSITION_X: 100,
  POSITION_Y: 100,
  WIDTH: 280,
  HEIGHT: 200,
  Z_INDEX: 0,
  IS_COLLAPSED: false,
} as const;

/**
 * Position offset when creating multiple stickies
 * (so they don't stack exactly on top of each other)
 */
export const TAB_STICKY_POSITION_OFFSET = 30;

/**
 * Debounce delays for auto-save operations
 */
export const TAB_STICKY_DEBOUNCE = {
  CONTENT: 500,      // 500ms for content changes
  POSITION: 300,     // 300ms for position/size changes
  COLLAPSE: 0,       // Immediate for collapse toggle
} as const;

/**
 * API endpoints for tab stickies
 */
export const TAB_STICKIES_API = {
  BASE: '/tab-stickies',
  SYNC: '/tab-stickies/sync',
} as const;
